/**
 * Panel de Internal → Seguridad (Vault F2): política de MFA (fecha desde la
 * que Internal exige 2SV de Google, editable sin deploy) y estado por persona
 * (Directory API en vivo).
 */
import { useEffect, useState } from 'react';
import { CalendarClock, ShieldCheck, ShieldOff, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, LoaderBlock, StatCard, fmtDateTime, fmtNum } from './shared';
import { VaultProfilesCard } from './VaultProfilesCard';

/** input type=date (YYYY-MM-DD) ↔ ISO a las 00:00 de Argentina (UTC-3). */
const toIso = (day: string) => (day ? new Date(`${day}T03:00:00.000Z`).toISOString() : null);
const toDay = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

export function SecurityTab() {
  const { t, i18n } = useTranslation();
  const utils = trpc.useUtils();
  const enforcement = trpc.internal.getMfaEnforcement.useQuery(undefined, { staleTime: 30_000 });
  const report = trpc.internal.mfaReport.useQuery(undefined, { staleTime: 5 * 60_000, retry: false });

  const [day, setDay] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (enforcement.data) {
      setDay(toDay(enforcement.data.enforceFrom));
      setNote(enforcement.data.note ?? '');
    }
  }, [enforcement.data]);

  const save = trpc.internal.setMfaEnforcement.useMutation({
    onSuccess: () => {
      utils.internal.getMfaEnforcement.invalidate();
      utils.profile.getMfaStatus.invalidate();
      toast.success(t('internal.securitytab.saved', 'Política guardada'));
    },
    onError: (e) => toast.error(e.message),
  });

  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  const rows = report.data ?? [];
  const missing = rows.filter((r) => r.enrolled === false);
  const enrolled = rows.filter((r) => r.enrolled === true);
  const unknown = rows.filter((r) => r.enrolled === null);
  // ~350 cuentas de Workspace: los contadores van sobre `rows`, la tabla pagina.
  const { pageItems, footer: paginationFooter } = usePagination(rows, 25);

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
            <CalendarClock className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-semibold">{t('internal.securitytab.enforce_title', 'Exigir 2SV de Google para entrar a Internal')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('internal.securitytab.enforce_hint', 'Pasada esta fecha, quien no tenga la verificación en dos pasos activada ve una pantalla de bloqueo hasta activarla. El Vault la exige siempre, con o sin fecha.')}
            </p>
          </div>
        </div>

        {enforcement.isLoading ? (
          <LoaderBlock />
        ) : (
          <>
            <p className="text-sm">
              {!enforcement.data?.enforceFrom
                ? t('internal.securitytab.not_set', 'Sin fecha: hoy solo el Vault la exige.')
                : enforcement.data.enforcedNow
                  ? t('internal.securitytab.active_since', 'Obligatoria desde el {{date}}.', { date: fmtDay(enforcement.data.enforceFrom) })
                  : t('internal.securitytab.scheduled', 'Se va a exigir a partir del {{date}}.', { date: fmtDay(enforcement.data.enforceFrom) })}
              {enforcement.data?.updatedBy && (
                <span className="ml-2 text-muted-foreground">
                  {t('internal.securitytab.updated_by', 'Actualizado por {{by}} el {{at}}', {
                    by: enforcement.data.updatedBy,
                    at: fmtDateTime(enforcement.data.updatedAt ?? ''),
                  })}
                </span>
              )}
            </p>
            <div className="grid gap-3 sm:grid-cols-[180px_1fr_auto_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor="mfa-enforce-from">{t('internal.securitytab.enforce_from', 'Obligatoria desde')}</Label>
                <Input id="mfa-enforce-from" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mfa-note">{t('internal.securitytab.note', 'Nota')}</Label>
                <Input id="mfa-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
              </div>
              <Button onClick={() => save.mutate({ enforceFrom: toIso(day), note })} disabled={save.isPending || !day}>
                {t('internal.securitytab.save', 'Guardar')}
              </Button>
              <Button
                variant="outline"
                onClick={() => save.mutate({ enforceFrom: null, note })}
                disabled={save.isPending || !enforcement.data?.enforceFrom}
              >
                {t('internal.securitytab.clear', 'Quitar fecha')}
              </Button>
            </div>
          </>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          icon={UserX}
          label={t('internal.securitytab.missing', 'Sin 2SV')}
          value={fmtNum(missing.length)}
          tone={missing.length > 0 ? 'rose' : 'green'}
        />
        <StatCard icon={UserCheck} label={t('internal.securitytab.enrolled', 'Con 2SV')} value={fmtNum(enrolled.length)} tone="green" />
        <StatCard icon={ShieldOff} label={t('internal.securitytab.unknown', 'Sin dato')} value={fmtNum(unknown.length)} />
      </div>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">{t('internal.securitytab.report_title', 'Estado por persona')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('internal.securitytab.report_hint', 'Cuentas activas de Workspace, consultadas en vivo a la Directory API.')}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => report.refetch()} disabled={report.isFetching}>
            {t('internal.securitytab.refresh', 'Actualizar')}
          </Button>
        </div>

        {report.isLoading ? (
          <LoaderBlock />
        ) : report.isError ? (
          <EmptyState title={t('internal.securitytab.error', 'No se pudo leer la Directory API')} />
        ) : missing.length === 0 && unknown.length === 0 ? (
          <EmptyState title={t('internal.securitytab.all_good', 'Todas las cuentas activas tienen 2SV.')} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('internal.securitytab.col_person', 'Persona')}</TableHead>
                  <TableHead>{t('internal.securitytab.col_status', '2SV')}</TableHead>
                  <TableHead>{t('internal.securitytab.col_ou', 'Unidad')}</TableHead>
                  <TableHead>{t('internal.securitytab.col_last_login', 'Último login')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((r) => (
                  <TableRow key={r.email}>
                    <TableCell>
                      <div className="font-medium">{r.name || r.email}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </TableCell>
                    <TableCell>
                      {r.enrolled === true ? (
                        <Badge variant="secondary" className="gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          {t('internal.securitytab.status_enrolled', 'Activada')}
                        </Badge>
                      ) : r.enrolled === false ? (
                        <Badge variant="destructive">{t('internal.securitytab.status_missing', 'Sin activar')}</Badge>
                      ) : (
                        <Badge variant="outline">{t('internal.securitytab.status_unknown', 'Sin dato')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.orgUnitPath ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.lastLoginTime ? fmtDateTime(r.lastLoginTime) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {paginationFooter}
          </div>
        )}
      </Card>

      {/* Passphrases del Vault: IT resetea desde acá (vaultAdmin.resetUserPassphrase). */}
      <VaultProfilesCard />
    </div>
  );
}
